import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "react-query";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Boxes,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  Archive,
  Database,
  Download,
  History,
  Eye,
  EyeOff,
  GitBranch,
  Maximize2,
  Pencil,
  Play,
  Plus,
  RotateCcw,
  Save,
  Shield,
  Sparkles,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import dagre from "@dagrejs/dagre";
import ReactFlow, {
  Background,
  Controls,
  Edge,
  MarkerType,
  Node,
  Position,
} from "reactflow";
import "reactflow/dist/style.css";
import { apiService } from "../services/api";
import {
  Repository,
  canCreateThreatModel,
  canEditThreatModel,
  canManageThreatModelFamily,
  canManageThreatModelPrompts,
  type ThreatModelVersion,
} from "../types";
import { useAuth } from "../hooks/useAuth";
import toast from "react-hot-toast";
import ThreatModelVersionDialog from "../components/ThreatModelVersionDialog";
import { SEMVER_HINT, normalizeSemver } from "../utils/threatModelVersions";

type ComponentKind =
  | "frontend"
  | "backend"
  | "database"
  | "cache"
  | "queue"
  | "external"
  | "other";
type DataClassification = "public" | "internal" | "confidential" | "regulated";
type FlowGroupingMode = "source" | "destination";
type FlowGroupingSelection = Record<FlowGroupingMode, boolean>;
type RepositoryInputMode = "catalog" | "text";

const NODE_WIDTH = 240;
const NODE_HEIGHT = 76;

interface RepositoryAttachment {
  mode: RepositoryInputMode;
  repositoryId: string;
  repositoryFullName: string;
  freeText: string;
}

interface PlatformComponent {
  id: string;
  name: string;
  kind: ComponentKind;
  managedByPlatform: boolean;
}

interface UserComponent {
  id: string;
  name: string;
  kind: ComponentKind;
  notes: string;
  sourceRepository: RepositoryAttachment;
  deploymentRepository: RepositoryAttachment;
  disabled?: boolean;
}

interface ThreatAsset {
  id: string;
  name: string;
  classification: DataClassification;
  owner: string;
  notes: string;
}

interface DataFlow {
  id: string;
  fromComponentId: string;
  toComponentId: string;
  protocol: string;
  authMethod: string;
  carriesSensitiveData: boolean;
  notes: string;
  disabled?: boolean;
}

interface TrackedRepository {
  id: string;
  repositoryRole: "source" | "deployment" | "infra" | "other";
  repository: RepositoryAttachment;
  namespace: string;
  notes: string;
}

interface ThreatModelDraft {
  modelName: string;
  scope: string;
  environment: string;
  assumptions: string;
  repositories: TrackedRepository[];
  components: UserComponent[];
  assets: ThreatAsset[];
  dataFlows: DataFlow[];
  updatedAt: string;
}

type SeverityLevel = "Critical" | "High" | "Medium" | "Low";
type ControlType = "Preventive" | "Detective" | "Corrective";

interface StrideFinding {
  id: string;
  severity: SeverityLevel;
  threat: string;
  cwe: string;
  stride: "S" | "T" | "R" | "I" | "D" | "E";
  counterMeasures: string;
  findingName: string;
  controlType: ControlType;
  details: string;
}

interface ArchitectureGraphOptions {
  highlightedFlowId?: string;
  nodeWidth?: number;
  nodeHeight?: number;
  compact?: boolean;
  dimNonHighlighted?: boolean;
}

const PLATFORM_COMPONENTS: PlatformComponent[] = [
  {
    id: "pf-user",
    name: "User (Browser)",
    kind: "external",
    managedByPlatform: false,
  },
  {
    id: "pf-waf",
    name: "WAF / Front Door (Azure Front Door)",
    kind: "external",
    managedByPlatform: true,
  },
  {
    id: "pf-private-endpoint",
    name: "Private Endpoint",
    kind: "backend",
    managedByPlatform: true,
  },
  {
    id: "pf-gateway",
    name: "Cilium Gateway (Ingress)",
    kind: "backend",
    managedByPlatform: true,
  },
  {
    id: "pf-httproute",
    name: "HTTPRoute",
    kind: "backend",
    managedByPlatform: true,
  },
  {
    id: "pf-nginx",
    name: "Nginx Reverse Proxy (cluster-internal)",
    kind: "backend",
    managedByPlatform: true,
  },
  {
    id: "pf-secrets",
    name: "External Secrets Operator",
    kind: "backend",
    managedByPlatform: true,
  },
  {
    id: "pf-nat",
    name: "NAT Gateway (Egress)",
    kind: "external",
    managedByPlatform: true,
  },
  {
    id: "pf-acr",
    name: "acrp00devxeuwprd.azurecr.io (Container Registry)",
    kind: "external",
    managedByPlatform: true,
  },
];

const COMPONENT_ID_UI = "app-prod-atlas-ui";
const COMPONENT_ID_API = "app-prod-atlas-api";

const DEFAULT_DRAFT: ThreatModelDraft = {
  modelName: "",
  scope: "",
  environment: "",
  assumptions: "",
  repositories: [],
  components: [],
  assets: [],
  dataFlows: [],
  updatedAt: new Date().toISOString(),
};

const STEPS = [
  "Scope",
  "Prompts",
  "Repositories",
  "Components",
  "Assets",
  "Data Flows",
  "Review",
  "Architecture",
  "Analysis",
] as const;

const STEP_ICONS: Record<(typeof STEPS)[number], typeof Shield> = {
  Scope: Shield,
  Prompts: Sparkles,
  Repositories: GitBranch,
  Components: Boxes,
  Assets: Database,
  "Data Flows": ArrowRight,
  Review: CheckCircle2,
  Architecture: Boxes,
  Analysis: Shield,
};

function defaultRepositoryAttachment(
  mode: RepositoryInputMode = "catalog",
): RepositoryAttachment {
  return {
    mode,
    repositoryId: "",
    repositoryFullName: "",
    freeText: "",
  };
}

function hidesRepositoryAttachments(kind: ComponentKind): boolean {
  return kind === "database" || kind === "external" || kind === "cache";
}

function normalizeComponentFromArtifact(c: any): UserComponent {
  const kind: ComponentKind =
    c.kind === "queue"
      ? "queue"
      : c.kind === "cache"
        ? "cache"
        : c.kind === "frontend"
          ? "frontend"
          : c.kind === "database"
            ? "database"
            : c.kind === "external"
              ? "external"
              : "backend";

  const emptyRepo: RepositoryAttachment = {
    mode: "text",
    repositoryId: "",
    repositoryFullName: "",
    freeText: "",
  };
  const sourceRepository: RepositoryAttachment =
    typeof c.sourceRepository === "object" && c.sourceRepository?.mode
      ? c.sourceRepository
      : typeof c.sourceRepository === "string" && c.sourceRepository
        ? {
            mode: "text",
            repositoryId: "",
            repositoryFullName: "",
            freeText: c.sourceRepository,
          }
        : emptyRepo;

  const deploymentRepository: RepositoryAttachment =
    typeof c.deploymentRepository === "object" && c.deploymentRepository?.mode
      ? c.deploymentRepository
      : emptyRepo;

  return {
    id: c.id,
    name: c.name || "",
    kind,
    notes:
      (c.notes || "") +
      (c.evidence?.length ? `\n\nEvidence:\n${c.evidence.join("\n")}` : ""),
    sourceRepository,
    deploymentRepository,
    disabled: c.disabled || false,
  };
}

function normalizeDataFlowFromArtifact(f: any): DataFlow {
  return {
    id: f.id,
    fromComponentId: f.fromComponentId,
    toComponentId: f.toComponentId,
    protocol: f.protocol || "",
    authMethod: f.authMethod || "",
    carriesSensitiveData: f.carriesSensitiveData || false,
    notes:
      (f.notes || "") +
      (f.evidence?.length ? `\n\nEvidence:\n${f.evidence.join("\n")}` : ""),
    disabled: f.disabled || false,
  };
}

function normalizeAssetFromArtifact(a: any): ThreatAsset {
  const classification: DataClassification =
    a.classification === "restricted"
      ? "regulated"
      : a.classification === "confidential"
        ? "confidential"
        : a.classification === "internal"
          ? "internal"
          : "public";
  return {
    id: a.id,
    name: a.name || "",
    classification,
    owner: a.owner || "",
    notes: a.notes || "",
  };
}

function kindLabel(kind: ComponentKind): string {
  switch (kind) {
    case "frontend":
      return "Frontend";
    case "backend":
      return "Backend";
    case "database":
      return "Database";
    case "cache":
      return "Cache";
    case "queue":
      return "Queue";
    case "external":
      return "External";
    default:
      return "Other";
  }
}

function componentDisplayName(componentId: string, name: string): string {
  if (componentId === COMPONENT_ID_UI) {
    return "Frontend UI (prod-atlas-ui)";
  }
  if (componentId === COMPONENT_ID_API) {
    return "Backend API (prod-atlas-api)";
  }

  return name;
}

function resolveRepositoryAttachment(
  value: string,
  repositories: Repository[],
): RepositoryAttachment {
  const trimmed = value.trim();
  if (!trimmed) {
    return defaultRepositoryAttachment("catalog");
  }

  const selected = repositories.find(
    (repository) => repository.fullName === trimmed,
  );
  if (selected) {
    return {
      mode: "catalog",
      repositoryId: selected.id,
      repositoryFullName: selected.fullName,
      freeText: "",
    };
  }

  return {
    mode: "text",
    repositoryId: "",
    repositoryFullName: trimmed,
    freeText: trimmed,
  };
}

function kindNodeClasses(kind: ComponentKind): string {
  switch (kind) {
    case "frontend":
      return "border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/30";
    case "backend":
      return "border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20";
    case "database":
      return "border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20";
    case "external":
      return "border-violet-200 dark:border-violet-700 bg-violet-50 dark:bg-violet-900/20";
    case "cache":
      return "border-emerald-200 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20";
    case "queue":
      return "border-cyan-200 dark:border-cyan-700 bg-cyan-50 dark:bg-cyan-900/20";
    default:
      return "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900";
  }
}

function buildArchitectureGraph(
  components: Array<{
    id: string;
    name: string;
    kind: ComponentKind;
    managedByPlatform: boolean;
  }>,
  dataFlows: DataFlow[],
  options: ArchitectureGraphOptions = {},
): { nodes: Node[]; edges: Edge[] } {
  const {
    highlightedFlowId,
    nodeWidth = NODE_WIDTH,
    nodeHeight = NODE_HEIGHT,
    compact = false,
    dimNonHighlighted = false,
  } = options;
  const graph = new dagre.graphlib.Graph();
  graph.setGraph({
    rankdir: "LR",
    nodesep: 60,
    ranksep: 110,
    marginx: 20,
    marginy: 20,
  });
  graph.setDefaultEdgeLabel(() => ({}));

  const highlightedFlow = dataFlows.find(
    (flow) => flow.id === highlightedFlowId,
  );
  const highlightedNodeIds = new Set(
    highlightedFlow
      ? [highlightedFlow.fromComponentId, highlightedFlow.toComponentId]
      : [],
  );

  components.forEach((component) => {
    graph.setNode(component.id, { width: nodeWidth, height: nodeHeight });
  });

  dataFlows.forEach((flow) => {
    if (!flow.fromComponentId || !flow.toComponentId) {
      return;
    }
    graph.setEdge(flow.fromComponentId, flow.toComponentId);
  });

  dagre.layout(graph);

  const nodes: Node[] = components.map((component) => {
    const position = graph.node(component.id);
    const classes = kindNodeClasses(component.kind);
    return {
      id: component.id,
      position: {
        x: position ? position.x - nodeWidth / 2 : 0,
        y: position ? position.y - nodeHeight / 2 : 0,
      },
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
      data: {
        label: (
          <div
            className={`rounded-md border px-3 py-2 shadow-sm ${classes} ${
              dimNonHighlighted &&
              highlightedNodeIds.size > 0 &&
              !highlightedNodeIds.has(component.id)
                ? "opacity-45"
                : ""
            }`}
            style={{ width: compact ? 190 : 220 }}
          >
            <p
              className={`${compact ? "text-xs" : "text-sm"} font-semibold text-gray-900 dark:text-gray-100 leading-tight`}
            >
              {component.name}
            </p>
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
              {kindLabel(component.kind)}
              {component.managedByPlatform ? " • Platform" : ""}
            </p>
          </div>
        ),
      },
      draggable: false,
      selectable: false,
    };
  });

  const edges: Edge[] = dataFlows
    .filter((flow) => flow.fromComponentId && flow.toComponentId)
    .map((flow) => {
      const isHighlighted = flow.id === highlightedFlowId;
      const shouldDim =
        dimNonHighlighted && highlightedFlowId && !isHighlighted;

      return {
        id: flow.id,
        source: flow.fromComponentId,
        target: flow.toComponentId,
        type: "smoothstep",
        animated: isHighlighted ? true : flow.carriesSensitiveData,
        markerEnd: { type: MarkerType.ArrowClosed },
        label: `${flow.protocol} • ${flow.authMethod}`,
        labelBgPadding: [6, 2],
        labelBgBorderRadius: 4,
        labelBgStyle: {
          fill: "#ffffff",
          fillOpacity: shouldDim ? 0.45 : 0.9,
        },
        style: {
          stroke: isHighlighted
            ? "#0f766e"
            : flow.carriesSensitiveData
              ? "#2563eb"
              : "#6b7280",
          strokeWidth: isHighlighted
            ? 3
            : flow.carriesSensitiveData
              ? 2.2
              : 1.6,
          opacity: shouldDim ? 0.3 : 1,
        },
      };
    });

  return { nodes, edges };
}

function extractFilePaths(details: string): string[] {
  const matches = details.match(/[\w\-./]+\.\w{1,5}(?::\d[\d\-,]*)?/g);
  if (!matches) return [];
  const seen = new Set<string>();
  return matches
    .filter(
      (m) =>
        m.includes("/") ||
        m.includes(".go") ||
        m.includes(".ts") ||
        m.includes(".yaml") ||
        m.includes(".json"),
    )
    .filter((m) => !m.startsWith("e.g.") && !m.startsWith("v5."))
    .filter((m) => {
      if (seen.has(m)) return false;
      seen.add(m);
      return true;
    })
    .slice(0, 4);
}

function severityBadgeClasses(severity: SeverityLevel): string {
  switch (severity) {
    case "Critical":
      return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300";
    case "High":
      return "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300";
    case "Medium":
      return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
  }
}

// Keyed by id so switching versions starts from a fresh editor state.
export default function ThreatModellingPage() {
  const { id } = useParams<{ id: string }>();
  return <ThreatModellingEditor key={id ?? "create"} />;
}

function ThreatModellingEditor() {
  const { id: threatModelId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [initialVersion, setInitialVersion] = useState("1.0.0");
  const [showNewVersion, setShowNewVersion] = useState(false);

  const [stepIndex, setStepIndex] = useState(0);
  const [draft, setDraft] = useState<ThreatModelDraft>(DEFAULT_DRAFT);
  const [selectedFlowId, setSelectedFlowId] = useState<string | null>(null);

  const [enabledFlowGroupings, setEnabledFlowGroupings] =
    useState<FlowGroupingSelection>({
      source: true,
      destination: false,
    });
  const [flowComponentFilterId, setFlowComponentFilterId] =
    useState<string>("all");
  const [showDisabledFlows, setShowDisabledFlows] = useState(false);
  const [selectedFinding, setSelectedFinding] = useState<StrideFinding | null>(
    null,
  );
  const [fullscreenDiagram, setFullscreenDiagram] = useState<
    "edge" | "downstream" | null
  >(null);
  const [editingAssetId, setEditingAssetId] = useState<string | null>(null);
  const [editingComponentId, setEditingComponentId] = useState<string | null>(
    null,
  );
  const [editingFlowId, setEditingFlowId] = useState<string | null>(null);
  const [fullscreenFlowGroupKey, setFullscreenFlowGroupKey] = useState<
    string | null
  >(null);
  const [strideFindings, setStrideFindings] = useState<StrideFinding[]>([]);
  const [strideSeverityFilter, setStrideSeverityFilter] = useState<
    SeverityLevel | "All"
  >("All");
  const [strideSearchText, setStrideSearchText] = useState("");
  const [flowSortColumn, setFlowSortColumn] = useState<
    "from" | "to" | "protocol" | "auth" | "sensitive"
  >("from");
  const [flowSortAsc, setFlowSortAsc] = useState(true);

  const { data: repositoriesData, isLoading: repositoriesLoading } = useQuery(
    ["threat-modelling-repositories"],
    () => apiService.getRepositories({ limit: 10000 }),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
  const repositories: Repository[] =
    repositoriesData?.data?.data?.repositories ?? [];

  // Load existing threat model metadata from API
  const { data: threatModelData } = useQuery(
    ["threat-model", threatModelId],
    () => apiService.getThreatModel(threatModelId!),
    {
      enabled: !!threatModelId,
      refetchOnWindowFocus: false,
    },
  );

  const threatModel: ThreatModelVersion | undefined =
    threatModelData?.data?.data;
  const canEdit = threatModelId
    ? canEditThreatModel(user, threatModel?.created_by_id)
    : canCreateThreatModel(user);
  const canRename =
    !threatModelId ||
    canManageThreatModelFamily(user, threatModel?.family?.created_by_id);

  const { data: versionsData } = useQuery(
    ["threat-model-versions", threatModelId],
    () => apiService.getThreatModelVersions(threatModelId!),
    { enabled: !!threatModelId, refetchOnWindowFocus: false },
  );
  const siblingVersions: ThreatModelVersion[] = versionsData?.data?.data ?? [];
  const canEditPrompts = canManageThreatModelPrompts(user);

  // Determine which artifact to load based on phase+status
  const { data: activeArtifactData } = useQuery(
    ["active-artifact", threatModelId],
    () => apiService.getActiveArtifact(threatModelId!),
    {
      enabled: !!threatModelId,
      refetchOnWindowFocus: false,
    },
  );

  const activeArtifactInfo = activeArtifactData?.data?.data as
    | { phase: string; status: string; artifact: string; editable: boolean }
    | undefined;

  // Load the active artifact content
  useQuery(
    ["artifact-content", threatModelId, activeArtifactInfo?.artifact],
    () => apiService.getArtifact(threatModelId!, activeArtifactInfo!.artifact),
    {
      enabled: !!threatModelId && !!activeArtifactInfo?.artifact,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        const artifact = response?.data?.data;
        if (!artifact?.content) return;
        try {
          const content = JSON.parse(artifact.content);
          const model = content.content ?? content;
          setDraft((prev) => ({
            ...prev,
            modelName: threatModel?.name || prev.modelName,
            scope: threatModel?.description || prev.scope,
            environment:
              content.environment || model.environment || prev.environment,
            assumptions:
              content.assumptions || model.assumptions || prev.assumptions,
            repositories: model.repositories || prev.repositories,
            components: (model.components || prev.components).map(
              normalizeComponentFromArtifact,
            ),
            assets: (model.assets || prev.assets).map(
              normalizeAssetFromArtifact,
            ),
            dataFlows: (model.dataFlows || prev.dataFlows).map(
              normalizeDataFlowFromArtifact,
            ),
          }));
          if (content.strideResults) {
            setStrideFindings(content.strideResults);
          }
        } catch {
          /* ignore parse errors */
        }
      },
    },
  );

  // Fallback: when no artifact exists yet (phase=created), load from tm.config
  useEffect(() => {
    if (!threatModel || activeArtifactInfo?.artifact) return;
    const tm = threatModel;
    if (tm.config) {
      try {
        const config =
          typeof tm.config === "string" ? JSON.parse(tm.config) : tm.config;
        setDraft((prev) => ({
          ...prev,
          modelName: tm.name || prev.modelName,
          scope: tm.description || prev.scope,
          environment: config.environment || prev.environment,
          assumptions: config.assumptions || prev.assumptions,
          repositories: config.repositories || prev.repositories,
          components: (config.components || prev.components).map(
            normalizeComponentFromArtifact,
          ),
          assets: (config.assets || prev.assets).map(
            normalizeAssetFromArtifact,
          ),
          dataFlows: (config.dataFlows || prev.dataFlows).map(
            normalizeDataFlowFromArtifact,
          ),
        }));
      } catch {
        /* ignore */
      }
    } else if (tm.name) {
      setDraft((prev) => ({
        ...prev,
        modelName: tm.name || prev.modelName,
        scope: tm.description || prev.scope,
      }));
    }
  }, [threatModel, activeArtifactInfo]);

  const saveMutation = useMutation(
    (payload: {
      name: string;
      description?: string;
      config: string;
      version?: string;
    }) => {
      if (threatModelId) {
        return apiService.updateThreatModel(threatModelId, {
          name: payload.name,
          ...(payload.description != null && {
            description: payload.description,
          }),
          config: JSON.parse(payload.config),
        });
      }
      return apiService.createThreatModel(payload);
    },
    {
      onSuccess: (res) => {
        toast.success(threatModelId ? "Saved" : "Threat model created");
        if (!threatModelId) {
          const newId = res?.data?.data?.id;
          if (newId) navigate(`/threat-modelling/${newId}`, { replace: true });
        }
      },
      onError: (err: any) => {
        toast.error(
          err?.response?.data?.error || "Failed to save threat model",
        );
      },
    },
  );

  const saveAndAnalyseMutation = useMutation(
    async (payload: {
      name: string;
      description?: string;
      config: string;
      version?: string;
    }) => {
      let id = threatModelId;
      if (id) {
        await apiService.updateThreatModel(id, {
          name: payload.name,
          ...(payload.description != null && {
            description: payload.description,
          }),
          config: JSON.parse(payload.config),
        });
      } else {
        const res = await apiService.createThreatModel(payload);
        id = res.data?.data?.id;
      }
      if (!id) throw new Error("No threat model ID");
      await apiService.triggerStage(id, { autonomous: true });
      return id;
    },
    {
      onSuccess: () => {
        toast.success("Saved and analysis triggered — agent is processing...");
        navigate("/threat-modelling");
      },
      onError: (err: any) => {
        if (err?.message?.includes("trigger")) {
          toast.error("Saved but failed to trigger analysis. Is NATS running?");
        } else {
          toast.error(
            err?.response?.data?.error || "Failed to save threat model",
          );
        }
      },
    },
  );

  // Session prompts for the Prompts wizard step
  const { data: sessionPromptsData, refetch: refetchPrompts } = useQuery(
    ["session-prompts", threatModelId],
    () => apiService.getSessionPrompts(threatModelId!),
    {
      enabled: !!threatModelId && stepIndex === 1,
      refetchOnWindowFocus: false,
    },
  );
  const sessionPrompts: Array<{
    prompt_slug: string;
    name: string;
    description: string;
    content: string;
    is_override: boolean;
    variables: any;
  }> = sessionPromptsData?.data?.data ?? [];

  const [expandedPromptSlug, setExpandedPromptSlug] = useState<string | null>(
    null,
  );
  const [promptEdits, setPromptEdits] = useState<Record<string, string>>({});

  const promptSaveMutation = useMutation(
    ({ slug, content }: { slug: string; content: string }) =>
      apiService.upsertSessionPrompt(threatModelId!, slug, content),
    {
      onSuccess: () => {
        refetchPrompts();
        toast.success("Prompt saved");
      },
      onError: () => {
        toast.error("Failed to save prompt");
      },
    },
  );

  const promptResetMutation = useMutation(
    (slug: string) => apiService.deleteSessionPrompt(threatModelId!, slug),
    {
      onSuccess: () => {
        refetchPrompts();
        toast.success("Prompt reset to default");
      },
      onError: () => {
        toast.error("Failed to reset prompt");
      },
    },
  );

  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const resetModelMutation = useMutation(
    () => apiService.resetThreatModel(threatModelId!),
    {
      onSuccess: () => {
        toast.success("Threat model reset — all stages and findings cleared");
        setShowResetConfirm(false);
        window.location.reload();
      },
      onError: () => {
        toast.error("Failed to reset threat model");
      },
    },
  );

  const handleSaveToBackend = () => {
    const config = JSON.stringify({
      environment: draft.environment,
      assumptions: draft.assumptions,
      repositories: draft.repositories,
      components: draft.components,
      assets: draft.assets,
      dataFlows: draft.dataFlows,
    });
    saveMutation.mutate({
      name: draft.modelName || "Untitled Threat Model",
      description: draft.scope,
      config,
      ...(!threatModelId && { version: initialVersion.trim() }),
    });
  };

  const handleSaveAndAnalyse = () => {
    const config = JSON.stringify({
      environment: draft.environment,
      assumptions: draft.assumptions,
      repositories: draft.repositories,
      components: draft.components,
      assets: draft.assets,
      dataFlows: draft.dataFlows,
    });
    saveAndAnalyseMutation.mutate({
      name: draft.modelName || "Untitled Threat Model",
      description: draft.scope,
      config,
      ...(!threatModelId && { version: initialVersion.trim() }),
    });
  };

  const hasPlatformTopology = useMemo(() => {
    const platformIds = new Set(PLATFORM_COMPONENTS.map((c) => c.id));
    return draft.dataFlows.some(
      (f) =>
        platformIds.has(f.fromComponentId) || platformIds.has(f.toComponentId),
    );
  }, [draft.dataFlows]);

  const allComponents = useMemo(
    () => [
      ...(hasPlatformTopology
        ? PLATFORM_COMPONENTS.map((component) => ({
            id: component.id,
            name: componentDisplayName(component.id, component.name),
            kind: component.kind,
          }))
        : []),
      ...draft.components.map((component) => ({
        id: component.id,
        name: componentDisplayName(component.id, component.name),
        kind: component.kind,
      })),
    ],
    [draft.components, hasPlatformTopology],
  );

  const diagramComponents = useMemo(
    () => [
      ...(hasPlatformTopology ? PLATFORM_COMPONENTS : []),
      ...draft.components
        .filter((component) => !component.disabled)
        .map((component) => ({
          id: component.id,
          name: componentDisplayName(
            component.id,
            component.name || "Unnamed component",
          ),
          kind: component.kind,
          managedByPlatform: false,
        })),
    ],
    [draft.components, hasPlatformTopology],
  );

  const enabledFlows = useMemo(() => {
    const enabledComponentIds = new Set(
      draft.components.filter((c) => !c.disabled).map((c) => c.id),
    );
    return draft.dataFlows.filter(
      (f) =>
        !f.disabled &&
        enabledComponentIds.has(f.fromComponentId) &&
        enabledComponentIds.has(f.toComponentId),
    );
  }, [draft.components, draft.dataFlows]);

  const edgePathGraph = useMemo(() => {
    const edgePathNodeIds = new Set([
      "pf-user",
      "pf-waf",
      "pf-private-endpoint",
      "pf-gateway",
      "pf-httproute",
    ]);

    const flows = enabledFlows.filter(
      (flow) =>
        edgePathNodeIds.has(flow.fromComponentId) &&
        edgePathNodeIds.has(flow.toComponentId),
    );

    const components = diagramComponents.filter((component) =>
      edgePathNodeIds.has(component.id),
    );

    return buildArchitectureGraph(components, flows);
  }, [diagramComponents, enabledFlows]);

  const downstreamPathGraph = useMemo(() => {
    const outgoing = new Map<string, string[]>();
    enabledFlows.forEach((flow) => {
      if (!flow.fromComponentId || !flow.toComponentId) {
        return;
      }
      const next = outgoing.get(flow.fromComponentId) ?? [];
      next.push(flow.toComponentId);
      outgoing.set(flow.fromComponentId, next);
    });

    const reachable = new Set<string>(["pf-httproute"]);
    const queue: string[] = ["pf-httproute"];

    while (queue.length > 0) {
      const current = queue.shift();
      if (!current) {
        continue;
      }
      const nextNodes = outgoing.get(current) ?? [];
      nextNodes.forEach((nextNode) => {
        if (reachable.has(nextNode)) {
          return;
        }
        reachable.add(nextNode);
        queue.push(nextNode);
      });
    }

    const flows = enabledFlows.filter(
      (flow) =>
        reachable.has(flow.fromComponentId) &&
        reachable.has(flow.toComponentId),
    );
    const components = diagramComponents.filter((component) =>
      reachable.has(component.id),
    );

    return buildArchitectureGraph(components, flows);
  }, [diagramComponents, enabledFlows]);

  const fullGraph = useMemo(() => {
    const platformIds = new Set(PLATFORM_COMPONENTS.map((c) => c.id));
    const components = hasPlatformTopology
      ? diagramComponents
      : diagramComponents.filter((c) => !platformIds.has(c.id));
    return buildArchitectureGraph(components, enabledFlows);
  }, [diagramComponents, enabledFlows, hasPlatformTopology]);

  const effectiveSelectedFlowId =
    draft.dataFlows.find((flow) => flow.id === selectedFlowId)?.id ??
    draft.dataFlows[0]?.id ??
    null;

  const activeFlowGroupingModes = useMemo(
    () =>
      (["source", "destination"] as FlowGroupingMode[]).filter(
        (mode) => enabledFlowGroupings[mode],
      ),
    [enabledFlowGroupings],
  );

  const dataFlowGroups = useMemo(() => {
    const componentMap = new Map(
      diagramComponents.map((component) => [component.id, component]),
    );
    return activeFlowGroupingModes
      .flatMap((groupingMode) => {
        const grouped = new Map<string, DataFlow[]>();

        enabledFlows.forEach((flow) => {
          const groupId =
            groupingMode === "source"
              ? flow.fromComponentId
              : flow.toComponentId;
          const current = grouped.get(groupId) ?? [];
          current.push(flow);
          grouped.set(groupId, current);
        });

        return Array.from(grouped.entries()).map(([componentId, flows]) => {
          const nodeIds = new Set<string>([componentId]);
          flows.forEach((flow) => {
            nodeIds.add(
              groupingMode === "source"
                ? flow.toComponentId
                : flow.fromComponentId,
            );
          });

          const components = Array.from(nodeIds)
            .map((nodeId) => componentMap.get(nodeId))
            .filter(
              (
                component,
              ): component is {
                id: string;
                name: string;
                kind: ComponentKind;
                managedByPlatform: boolean;
              } => Boolean(component),
            );

          return {
            groupKey: `${groupingMode}:${componentId}`,
            groupingMode,
            groupingLabel:
              groupingMode === "source" ? "By source" : "By destination",
            componentId,
            componentName:
              componentMap.get(componentId)?.name ?? "Unknown component",
            flowCount: flows.length,
            flows,
            graph: buildArchitectureGraph(components, flows, {
              nodeWidth: 180,
              nodeHeight: 62,
              compact: true,
            }),
            relatedComponents: flows.map((flow) => ({
              id: flow.id,
              name:
                componentMap.get(
                  groupingMode === "source"
                    ? flow.toComponentId
                    : flow.fromComponentId,
                )?.name ?? "Unknown",
              protocol: flow.protocol,
              authMethod: flow.authMethod,
            })),
          };
        });
      })
      .sort((left, right) => {
        if (left.componentName === right.componentName) {
          return left.groupingMode.localeCompare(right.groupingMode);
        }
        return left.componentName.localeCompare(right.componentName);
      });
  }, [activeFlowGroupingModes, diagramComponents, enabledFlows]);

  const flowComponentFilterOptions = useMemo(
    () =>
      Array.from(
        new Map(
          dataFlowGroups.map((group) => [
            group.componentId,
            {
              value: group.componentId,
              label: group.componentName,
            },
          ]),
        ).values(),
      ).sort((left, right) => left.label.localeCompare(right.label)),
    [dataFlowGroups],
  );

  const selectedFlowComponentFilterLabel = useMemo(
    () =>
      flowComponentFilterOptions.find(
        (option) => option.value === flowComponentFilterId,
      )?.label ?? null,
    [flowComponentFilterId, flowComponentFilterOptions],
  );

  const visibleDataFlowGroups = useMemo(() => {
    if (flowComponentFilterId === "all") {
      return dataFlowGroups;
    }

    return dataFlowGroups.filter(
      (group) => group.componentId === flowComponentFilterId,
    );
  }, [dataFlowGroups, flowComponentFilterId]);

  const filteredFlowDiagram = useMemo(() => {
    if (flowComponentFilterId === "all" || visibleDataFlowGroups.length === 0) {
      return null;
    }

    const uniqueFlows = Array.from(
      new Map(
        visibleDataFlowGroups
          .flatMap((group) => group.flows)
          .map((flow) => [flow.id, flow]),
      ).values(),
    );
    const nodeIds = new Set<string>();

    uniqueFlows.forEach((flow) => {
      nodeIds.add(flow.fromComponentId);
      nodeIds.add(flow.toComponentId);
    });

    const components = diagramComponents.filter((component) =>
      nodeIds.has(component.id),
    );

    return {
      flowCount: uniqueFlows.length,
      relatedFlows: uniqueFlows.map((flow) => ({
        id: flow.id,
        label: `${allComponents.find((component) => component.id === flow.fromComponentId)?.name ?? "Unknown"} to ${allComponents.find((component) => component.id === flow.toComponentId)?.name ?? "Unknown"}`,
        protocol: flow.protocol,
        authMethod: flow.authMethod,
      })),
      graph: buildArchitectureGraph(components, uniqueFlows, {
        ...(effectiveSelectedFlowId
          ? { highlightedFlowId: effectiveSelectedFlowId }
          : {}),
        nodeWidth: 210,
        nodeHeight: 72,
        dimNonHighlighted: true,
      }),
    };
  }, [
    allComponents,
    diagramComponents,
    effectiveSelectedFlowId,
    flowComponentFilterId,
    visibleDataFlowGroups,
  ]);

  const selectedDisabledFlowDiagram = useMemo(() => {
    if (!effectiveSelectedFlowId) return null;
    const flow = draft.dataFlows.find((f) => f.id === effectiveSelectedFlowId);
    if (!flow) return null;
    const disabledComponentIds = new Set(
      draft.components.filter((c) => c.disabled).map((c) => c.id),
    );
    const isDisabledFlow =
      flow.disabled ||
      disabledComponentIds.has(flow.fromComponentId) ||
      disabledComponentIds.has(flow.toComponentId);
    if (!isDisabledFlow) return null;

    const nodeIds = new Set([flow.fromComponentId, flow.toComponentId]);
    const components = draft.components
      .filter((c) => nodeIds.has(c.id))
      .map((c) => ({
        id: c.id,
        name: c.name || "Unnamed",
        kind: c.kind,
        managedByPlatform: false,
      }));

    return {
      flow,
      graph: buildArchitectureGraph(components, [flow], {
        highlightedFlowId: flow.id,
        nodeWidth: 210,
        nodeHeight: 72,
      }),
    };
  }, [draft.components, draft.dataFlows, effectiveSelectedFlowId]);

  useEffect(() => {
    if (flowComponentFilterId === "all") {
      return;
    }

    const optionStillExists = dataFlowGroups.some(
      (group) => group.componentId === flowComponentFilterId,
    );
    if (!optionStillExists) {
      setFlowComponentFilterId("all");
    }
  }, [dataFlowGroups, flowComponentFilterId]);

  const toggleFlowGrouping = (mode: FlowGroupingMode) => {
    setEnabledFlowGroupings((current) => {
      const enabledCount = Object.values(current).filter(Boolean).length;
      if (current[mode] && enabledCount === 1) {
        return current;
      }

      return {
        ...current,
        [mode]: !current[mode],
      };
    });
  };

  // strideFindings is populated from imported threat-model-final.json via handleFileImport

  const updateDraft = (patch: Partial<ThreatModelDraft>) => {
    setDraft((prev) => ({
      ...prev,
      ...patch,
    }));
  };

  const handleFileImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const json = JSON.parse(ev.target?.result as string);
        const content = json.content;
        if (!content?.components) return;

        const mapKind = (kind: string): ComponentKind => {
          if (kind === "queue") return "queue";
          if (kind === "cache") return "cache";
          if (kind === "frontend") return "frontend";
          if (kind === "database") return "database";
          if (kind === "external") return "external";
          return "backend";
        };

        const mapClassification = (c: string): DataClassification => {
          if (c === "restricted") return "regulated";
          if (c === "confidential") return "confidential";
          if (c === "internal") return "internal";
          return "public";
        };

        const mapRepoRole = (
          scope?: string,
        ): TrackedRepository["repositoryRole"] => {
          if (scope === "source") return "source";
          if (scope?.includes("deployment")) return "deployment";
          if (scope === "infrastructure") return "infra";
          return "other";
        };

        const emptyRepo = (): RepositoryAttachment => ({
          mode: "text",
          repositoryId: "",
          repositoryFullName: "",
          freeText: "",
        });

        const deriveRepo = (
          evidence: string[] | undefined,
        ): RepositoryAttachment => {
          if (!evidence?.length || !content.repositories?.length)
            return emptyRepo();
          for (const repo of content.repositories) {
            if (evidence.some((e: string) => e.startsWith(repo.location))) {
              const name = repo.location.split("/").pop() || repo.location;
              return {
                mode: "text",
                repositoryId: "",
                repositoryFullName: "",
                freeText: name,
              };
            }
          }
          return emptyRepo();
        };

        const components: UserComponent[] = (content.components || []).map(
          (c: any) => ({
            id: c.id,
            name: c.name,
            kind: mapKind(c.kind),
            notes:
              (c.notes || "") +
              (c.evidence?.length
                ? `\n\nEvidence:\n${c.evidence.join("\n")}`
                : ""),
            sourceRepository:
              typeof c.sourceRepository === "string" && c.sourceRepository
                ? {
                    mode: "text",
                    repositoryId: "",
                    repositoryFullName: "",
                    freeText: c.sourceRepository,
                  }
                : deriveRepo(c.evidence),
            deploymentRepository: emptyRepo(),
            disabled: c.disabled || false,
          }),
        );

        const componentIds = new Set(components.map((c) => c.id));
        const dataFlows: DataFlow[] = (content.dataFlows || [])
          .filter(
            (f: any) =>
              componentIds.has(f.fromComponentId) ||
              componentIds.has(f.toComponentId),
          )
          .map((f: any) => ({
            id: f.id,
            fromComponentId: f.fromComponentId,
            toComponentId: f.toComponentId,
            protocol: f.protocol || "",
            authMethod: f.authMethod || "",
            carriesSensitiveData: f.carriesSensitiveData || false,
            notes:
              (f.notes || "") +
              (f.evidence?.length
                ? `\n\nEvidence:\n${f.evidence.join("\n")}`
                : ""),
            disabled: f.disabled || false,
          }));

        const assets: ThreatAsset[] = (content.assets || []).map((a: any) => ({
          id: a.id,
          name: a.name,
          classification: mapClassification(a.classification),
          owner: a.owner || "",
          notes: a.notes || "",
        }));

        const repositories: TrackedRepository[] = (
          content.repositories || []
        ).map((r: any) => ({
          id: r.id,
          repositoryRole: mapRepoRole(r.scope?.[0]),
          repository: {
            mode: "text" as RepositoryInputMode,
            repositoryId: "",
            repositoryFullName: r.location || "",
            freeText: r.location || "",
          },
          namespace: "",
          notes: "",
        }));

        updateDraft({
          modelName: json.scope || "Imported Model",
          scope: json.scope || "",
          environment: json.environment || "",
          assumptions: content.assumptions || "",
          components,
          dataFlows,
          assets,
          repositories,
        });

        const rawFindings: any[] =
          json.strideResults || content.strideResults || [];
        if (rawFindings.length > 0) {
          setStrideFindings(
            rawFindings.map((f: any, i: number) => ({
              id: f.id || `finding-${i}`,
              severity: f.severity || "Medium",
              threat: f.threat || "",
              cwe: f.cwe || "",
              stride: f.strideCategory || f.stride || "S",
              counterMeasures: f.counterMeasures || f.countermeasures || "",
              findingName: f.findingName || f.finding_name || "",
              controlType: f.controlType || f.control_type || "Preventive",
              details: f.details || "",
            })),
          );
        }
      } catch (err) {
        console.error("Failed to import file:", err);
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleExport = () => {
    const exported = {
      scope: draft.scope,
      environment: draft.environment,
      content: {
        repositories: draft.repositories.map((r) => ({
          id: r.id,
          location: r.repository.freeText || r.repository.repositoryFullName,
          scope: [r.repositoryRole],
        })),
        components: draft.components.map((c) => ({
          id: c.id,
          name: c.name,
          kind: c.kind,
          notes: c.notes,
          sourceRepository:
            c.sourceRepository.freeText ||
            c.sourceRepository.repositoryFullName ||
            "",
          disabled: c.disabled || false,
          verified: true,
          evidence: [],
        })),
        dataFlows: draft.dataFlows.map((f) => ({
          id: f.id,
          fromComponentId: f.fromComponentId,
          toComponentId: f.toComponentId,
          protocol: f.protocol,
          authMethod: f.authMethod,
          carriesSensitiveData: f.carriesSensitiveData,
          notes: f.notes,
          disabled: f.disabled || false,
        })),
        assets: draft.assets.map((a) => ({
          id: a.id,
          name: a.name,
          classification: a.classification,
          owner: a.owner,
          notes: a.notes,
        })),
        scope: draft.scope,
        assumptions: draft.assumptions || "",
      },
    };

    const blob = new Blob([JSON.stringify(exported, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "threat-model-user.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const updateComponent = (
    componentId: string,
    updater: (component: UserComponent) => UserComponent,
  ) => {
    updateDraft({
      components: draft.components.map((component) =>
        component.id === componentId ? updater(component) : component,
      ),
    });
  };

  const renderRepositoryAttachment = (
    component: UserComponent,
    field: "sourceRepository" | "deploymentRepository",
    title: string,
  ) => {
    const value = component[field];
    return (
      <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3">
        <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
          {title}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <input
            className="input md:col-span-4"
            list={`repo-options-${component.id}-${field}`}
            value={value.repositoryFullName}
            onChange={(event) =>
              updateComponent(component.id, (current) => ({
                ...current,
                [field]: resolveRepositoryAttachment(
                  event.target.value,
                  repositories,
                ),
              }))
            }
            disabled={repositoriesLoading}
            placeholder={
              repositoriesLoading
                ? "Loading repositories..."
                : "Search or type repository..."
            }
          />
          <div className="h-10 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex items-center text-xs text-gray-600 dark:text-gray-300">
            {value.repositoryId ? "Matched catalog" : "Using free text"}
          </div>
          <datalist id={`repo-options-${component.id}-${field}`}>
            {repositories.map((repository) => (
              <option key={repository.id} value={repository.fullName} />
            ))}
          </datalist>
        </div>
      </div>
    );
  };

  const addTrackedRepository = () => {
    updateDraft({
      repositories: [
        ...draft.repositories,
        {
          id: crypto.randomUUID(),
          repositoryRole: "source",
          repository: defaultRepositoryAttachment("catalog"),
          namespace: "",
          notes: "",
        },
      ],
    });
  };

  const renderStrideAnalysis = () => {
    const filtered = strideFindings.filter((f) => {
      if (strideSeverityFilter !== "All" && f.severity !== strideSeverityFilter)
        return false;
      if (strideSearchText) {
        const q = strideSearchText.toLowerCase();
        return (
          f.findingName.toLowerCase().includes(q) ||
          f.threat.toLowerCase().includes(q) ||
          f.cwe.toLowerCase().includes(q) ||
          f.details.toLowerCase().includes(q) ||
          f.counterMeasures.toLowerCase().includes(q)
        );
      }
      return true;
    });

    return (
      <div className="card space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            STRIDE Threat Analysis
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {filtered.length} of {strideFindings.length} findings
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            className="input w-[140px]"
            value={strideSeverityFilter}
            onChange={(e) =>
              setStrideSeverityFilter(e.target.value as SeverityLevel | "All")
            }
          >
            <option value="All">All severities</option>
            <option value="Critical">Critical</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
          <input
            className="input flex-1"
            placeholder="Search findings..."
            value={strideSearchText}
            onChange={(e) => setStrideSearchText(e.target.value)}
          />
        </div>
        <div className="overflow-x-auto rounded-md border border-gray-200 dark:border-gray-700">
          <table className="min-w-full table-fixed divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="w-[40px] px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                  #
                </th>
                <th className="w-[80px] px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                  Severity
                </th>
                <th className="w-[25%] px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                  Finding
                </th>
                <th className="w-[70px] px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                  CWE
                </th>
                <th className="w-[60px] px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                  STRIDE
                </th>
                <th className="w-[20%] px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                  File(s)
                </th>
                <th className="w-[30%] px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                  Counter Measures
                </th>
                <th className="w-[90px] px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
              {filtered.map((finding, idx) => (
                <tr key={finding.id}>
                  <td className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500 tabular-nums">
                    {idx + 1}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
                    <span
                      className={`inline-flex rounded px-2 py-0.5 text-xs font-semibold ${severityBadgeClasses(finding.severity)}`}
                    >
                      {finding.severity}
                    </span>
                  </td>
                  <td
                    className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300 max-w-[280px] truncate"
                    title={finding.threat}
                  >
                    {finding.findingName}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
                    {finding.cwe}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
                    {finding.stride}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 font-mono max-w-[200px]">
                    {extractFilePaths(finding.details).map((fp, i) => (
                      <div key={i} className="truncate" title={fp}>
                        {fp}
                      </div>
                    ))}
                  </td>
                  <td
                    className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300 max-w-[320px] truncate"
                    title={finding.counterMeasures}
                  >
                    {finding.counterMeasures}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => setSelectedFinding(finding)}
                    >
                      View details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    if (stepIndex === 0) {
      return (
        <div className="card space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Threat model name
            </label>
            <input
              className="input"
              value={draft.modelName}
              onChange={(event) =>
                updateDraft({ modelName: event.target.value })
              }
              placeholder="Prod Atlas - p00 baseline"
              disabled={!canRename}
              title={
                canRename
                  ? undefined
                  : "The name is shared by all versions and can only be changed by the model creator or a global admin."
              }
            />
          </div>
          {!threatModelId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Version
              </label>
              <input
                className="input"
                value={initialVersion}
                onChange={(event) => setInitialVersion(event.target.value)}
                placeholder="1.0.0"
              />
              {!normalizeSemver(initialVersion) && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                  {SEMVER_HINT}
                </p>
              )}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Scope
            </label>
            <textarea
              className="input min-h-[96px]"
              value={draft.scope}
              onChange={(event) => updateDraft({ scope: event.target.value })}
              placeholder="Example: front-door to backend API and database interactions"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Environment
              </label>
              <select
                className="input"
                value={draft.environment}
                onChange={(event) =>
                  updateDraft({ environment: event.target.value })
                }
              >
                <option value="p00">p00 (production)</option>
                <option value="d00">d00 (development)</option>
                <option value="custom">Custom</option>
              </select>
            </div>
          </div>
        </div>
      );
    }

    if (stepIndex === 2) {
      return (
        <div className="card space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Repositories for automated discovery
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                Add source, deployment, and infra repositories. Include
                namespace when known so runtime collection can be scoped.
              </p>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={addTrackedRepository}
            >
              <Plus className="h-4 w-4 mr-1" /> Add repository
            </button>
          </div>

          {draft.repositories.map((repo) => (
            <div
              key={repo.id}
              className="rounded-md border border-gray-200 dark:border-gray-700 p-4 space-y-3"
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
                <select
                  className="input lg:col-span-2"
                  value={repo.repositoryRole}
                  onChange={(event) =>
                    updateDraft({
                      repositories: draft.repositories.map((current) =>
                        current.id === repo.id
                          ? {
                              ...current,
                              repositoryRole: event.target
                                .value as TrackedRepository["repositoryRole"],
                            }
                          : current,
                      ),
                    })
                  }
                >
                  <option value="source">Source</option>
                  <option value="deployment">Deployment</option>
                  <option value="infra">Infra</option>
                  <option value="other">Other</option>
                </select>
                <input
                  className="input lg:col-span-5"
                  list={`tracked-repo-options-${repo.id}`}
                  value={repo.repository.repositoryFullName}
                  onChange={(event) =>
                    updateDraft({
                      repositories: draft.repositories.map((current) =>
                        current.id === repo.id
                          ? {
                              ...current,
                              repository: resolveRepositoryAttachment(
                                event.target.value,
                                repositories,
                              ),
                            }
                          : current,
                      ),
                    })
                  }
                  disabled={repositoriesLoading}
                  placeholder={
                    repositoriesLoading
                      ? "Loading repositories..."
                      : "Search or type repository..."
                  }
                />
                <div className="h-10 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex items-center text-xs text-gray-600 dark:text-gray-300 lg:col-span-2">
                  {repo.repository.repositoryId
                    ? "Matched catalog"
                    : "Using free text"}
                </div>
                <datalist id={`tracked-repo-options-${repo.id}`}>
                  {repositories.map((repository) => (
                    <option key={repository.id} value={repository.fullName} />
                  ))}
                </datalist>

                <button
                  type="button"
                  className="btn btn-outline lg:col-span-1"
                  onClick={() =>
                    updateDraft({
                      repositories: draft.repositories.filter(
                        (current) => current.id !== repo.id,
                      ),
                    })
                  }
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
                <input
                  className="input lg:col-span-4"
                  value={repo.namespace}
                  onChange={(event) =>
                    updateDraft({
                      repositories: draft.repositories.map((current) =>
                        current.id === repo.id
                          ? { ...current, namespace: event.target.value }
                          : current,
                      ),
                    })
                  }
                  placeholder="Namespace (optional), e.g. devx-k8s-applications-deploy"
                />
                <input
                  className="input lg:col-span-8"
                  value={repo.notes}
                  onChange={(event) =>
                    updateDraft({
                      repositories: draft.repositories.map((current) =>
                        current.id === repo.id
                          ? { ...current, notes: event.target.value }
                          : current,
                      ),
                    })
                  }
                  placeholder="Why this repository matters for threat modelling"
                />
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (stepIndex === 3) {
      const kindBadge = (kind: ComponentKind) => {
        const styles: Record<ComponentKind, string> = {
          frontend:
            "bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300",
          backend:
            "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
          database:
            "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
          cache:
            "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300",
          queue:
            "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300",
          external:
            "bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300",
          other:
            "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300",
        };
        return (
          <span
            className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${styles[kind]}`}
          >
            {kindLabel(kind)}
          </span>
        );
      };

      const editingComponent = draft.components.find(
        (c) => c.id === editingComponentId,
      );

      return (
        <div className="space-y-6">
          {hasPlatformTopology && (
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Platform components
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {PLATFORM_COMPONENTS.map((component) => (
                  <div
                    key={component.id}
                    className="rounded-md border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-900"
                  >
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {component.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {kindLabel(component.kind)} • Platform managed
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="card space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Application components
              </h3>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  const id = crypto.randomUUID();
                  updateDraft({
                    components: [
                      ...draft.components,
                      {
                        id,
                        name: "",
                        kind: "backend",
                        notes: "",
                        sourceRepository:
                          defaultRepositoryAttachment("catalog"),
                        deploymentRepository:
                          defaultRepositoryAttachment("catalog"),
                      },
                    ],
                  });
                  setEditingComponentId(id);
                }}
              >
                <Plus className="h-4 w-4 mr-1" /> Add component
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    <th className="py-2 w-24 font-medium">Actions</th>
                    <th className="py-2 pr-3 font-medium">Name</th>
                    <th className="py-2 pr-3 font-medium">Kind</th>
                    <th className="py-2 pr-3 font-medium">Repository</th>
                    <th className="py-2 pr-3 font-medium">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {draft.components.map((component) => (
                    <tr
                      key={component.id}
                      className={`transition-colors cursor-pointer ${component.disabled ? "opacity-40" : "hover:bg-gray-50 dark:hover:bg-gray-800/50"}`}
                      onDoubleClick={() => setEditingComponentId(component.id)}
                    >
                      <td className="py-2.5">
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${component.disabled ? "text-amber-500 hover:text-amber-600" : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"}`}
                            title={component.disabled ? "Enable" : "Disable"}
                            onClick={(e) => {
                              e.stopPropagation();
                              updateDraft({
                                components: draft.components.map((c) =>
                                  c.id === component.id
                                    ? { ...c, disabled: !c.disabled }
                                    : c,
                                ),
                              });
                            }}
                          >
                            {component.disabled ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            type="button"
                            className="p-1 rounded text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            title="Edit"
                            onClick={() => setEditingComponentId(component.id)}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            className="p-1 rounded text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            title="Delete"
                            onClick={() =>
                              updateDraft({
                                components: draft.components.filter(
                                  (current) => current.id !== component.id,
                                ),
                                dataFlows: draft.dataFlows.filter(
                                  (flow) =>
                                    flow.fromComponentId !== component.id &&
                                    flow.toComponentId !== component.id,
                                ),
                              })
                            }
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                      <td className="py-2.5 pr-3 font-medium text-gray-900 dark:text-gray-100">
                        {component.name || (
                          <span className="italic text-gray-400">Unnamed</span>
                        )}
                      </td>
                      <td className="py-2.5 pr-3">
                        {kindBadge(component.kind)}
                      </td>
                      <td className="py-2.5 pr-3 text-gray-500 dark:text-gray-400 max-w-[180px] truncate">
                        {component.sourceRepository.freeText ||
                          component.sourceRepository.repositoryFullName ||
                          "—"}
                      </td>
                      <td className="py-2.5 pr-3 text-gray-500 dark:text-gray-400 max-w-xs truncate">
                        {component.notes || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {editingComponent && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-3xl mx-4 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {editingComponent.name
                        ? "Edit component"
                        : "New component"}
                    </h3>
                    <button
                      type="button"
                      onClick={() => setEditingComponentId(null)}
                      className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="p-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Name
                      </label>
                      <input
                        className="input w-full"
                        value={editingComponent.name}
                        onChange={(event) =>
                          updateDraft({
                            components: draft.components.map((c) =>
                              c.id === editingComponent.id
                                ? { ...c, name: event.target.value }
                                : c,
                            ),
                          })
                        }
                        placeholder="Component name"
                        autoFocus
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Kind
                      </label>
                      <select
                        className="input w-full"
                        value={editingComponent.kind}
                        onChange={(event) =>
                          updateDraft({
                            components: draft.components.map((c) =>
                              c.id === editingComponent.id
                                ? {
                                    ...c,
                                    kind: event.target.value as ComponentKind,
                                    sourceRepository:
                                      hidesRepositoryAttachments(
                                        event.target.value as ComponentKind,
                                      )
                                        ? defaultRepositoryAttachment("catalog")
                                        : c.sourceRepository,
                                    deploymentRepository:
                                      hidesRepositoryAttachments(
                                        event.target.value as ComponentKind,
                                      )
                                        ? defaultRepositoryAttachment("catalog")
                                        : c.deploymentRepository,
                                  }
                                : c,
                            ),
                          })
                        }
                      >
                        <option value="frontend">Frontend</option>
                        <option value="backend">Backend</option>
                        <option value="database">Database</option>
                        <option value="cache">Cache</option>
                        <option value="queue">Queue</option>
                        <option value="external">External</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Notes
                      </label>
                      <textarea
                        className="input w-full min-h-[240px]"
                        value={editingComponent.notes}
                        onChange={(event) =>
                          updateDraft({
                            components: draft.components.map((c) =>
                              c.id === editingComponent.id
                                ? { ...c, notes: event.target.value }
                                : c,
                            ),
                          })
                        }
                        placeholder="Notes (optional)"
                      />
                    </div>
                    {!hidesRepositoryAttachments(editingComponent.kind) && (
                      <div className="space-y-3">
                        {renderRepositoryAttachment(
                          editingComponent,
                          "sourceRepository",
                          "Source repository",
                        )}
                        {renderRepositoryAttachment(
                          editingComponent,
                          "deploymentRepository",
                          "Deployment repository",
                        )}
                      </div>
                    )}
                  </div>
                  <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!editingComponent.disabled}
                        onChange={() =>
                          updateDraft({
                            components: draft.components.map((c) =>
                              c.id === editingComponent.id
                                ? { ...c, disabled: !c.disabled }
                                : c,
                            ),
                          })
                        }
                      />
                      Enabled in diagram
                    </label>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => setEditingComponentId(null)}
                    >
                      Done
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (stepIndex === 4) {
      const classificationBadge = (c: DataClassification) => {
        const styles: Record<DataClassification, string> = {
          public:
            "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
          internal:
            "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
          confidential:
            "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
          regulated:
            "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
        };
        return (
          <span
            className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${styles[c]}`}
          >
            {c.charAt(0).toUpperCase() + c.slice(1)}
          </span>
        );
      };

      const editingAsset = draft.assets.find((a) => a.id === editingAssetId);

      return (
        <div className="card space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Assets and sensitive data
            </h3>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                const id = crypto.randomUUID();
                updateDraft({
                  assets: [
                    ...draft.assets,
                    {
                      id,
                      name: "",
                      classification: "internal",
                      owner: "",
                      notes: "",
                    },
                  ],
                });
                setEditingAssetId(id);
              }}
            >
              <Plus className="h-4 w-4 mr-1" /> Add asset
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <th className="py-2 pr-3 font-medium">Name</th>
                  <th className="py-2 pr-3 font-medium">Classification</th>
                  <th className="py-2 pr-3 font-medium">Owner</th>
                  <th className="py-2 pr-3 font-medium">Notes</th>
                  <th className="py-2 w-20 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {draft.assets.map((asset) => (
                  <tr
                    key={asset.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                  >
                    <td className="py-2.5 pr-3 font-medium text-gray-900 dark:text-gray-100">
                      {asset.name || (
                        <span className="italic text-gray-400">Unnamed</span>
                      )}
                    </td>
                    <td className="py-2.5 pr-3">
                      {classificationBadge(asset.classification)}
                    </td>
                    <td className="py-2.5 pr-3 text-gray-600 dark:text-gray-300">
                      {asset.owner || "—"}
                    </td>
                    <td className="py-2.5 pr-3 text-gray-500 dark:text-gray-400 max-w-xs truncate">
                      {asset.notes || "—"}
                    </td>
                    <td className="py-2.5">
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          className="p-1 rounded text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          title="Edit"
                          onClick={() => setEditingAssetId(asset.id)}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          className="p-1 rounded text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          title="Delete"
                          onClick={() =>
                            updateDraft({
                              assets: draft.assets.filter(
                                (current) => current.id !== asset.id,
                              ),
                            })
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {editingAsset && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-3xl mx-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {editingAsset.name ? "Edit asset" : "New asset"}
                  </h3>
                  <button
                    type="button"
                    onClick={() => setEditingAssetId(null)}
                    className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="p-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Name
                    </label>
                    <input
                      className="input w-full"
                      value={editingAsset.name}
                      onChange={(event) =>
                        updateDraft({
                          assets: draft.assets.map((a) =>
                            a.id === editingAsset.id
                              ? { ...a, name: event.target.value }
                              : a,
                          ),
                        })
                      }
                      placeholder="Asset name"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Classification
                    </label>
                    <select
                      className="input w-full"
                      value={editingAsset.classification}
                      onChange={(event) =>
                        updateDraft({
                          assets: draft.assets.map((a) =>
                            a.id === editingAsset.id
                              ? {
                                  ...a,
                                  classification: event.target
                                    .value as DataClassification,
                                }
                              : a,
                          ),
                        })
                      }
                    >
                      <option value="public">Public</option>
                      <option value="internal">Internal</option>
                      <option value="confidential">Confidential</option>
                      <option value="regulated">Regulated</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Owner
                    </label>
                    <input
                      className="input w-full"
                      value={editingAsset.owner}
                      onChange={(event) =>
                        updateDraft({
                          assets: draft.assets.map((a) =>
                            a.id === editingAsset.id
                              ? { ...a, owner: event.target.value }
                              : a,
                          ),
                        })
                      }
                      placeholder="Owner"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Notes
                    </label>
                    <textarea
                      className="input w-full min-h-[240px]"
                      value={editingAsset.notes}
                      onChange={(event) =>
                        updateDraft({
                          assets: draft.assets.map((a) =>
                            a.id === editingAsset.id
                              ? { ...a, notes: event.target.value }
                              : a,
                          ),
                        })
                      }
                      placeholder="Notes"
                    />
                  </div>
                </div>
                <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setEditingAssetId(null)}
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    if (stepIndex === 5) {
      return (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-start xl:items-stretch">
          <div className="card space-y-3">
            <div className="flex flex-col gap-3 2xl:flex-row 2xl:items-start 2xl:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Data flows
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Show source groups, destination groups, or both depending on
                  the review pass you are doing.
                </p>
              </div>
              <div className="flex flex-col gap-2 lg:flex-row lg:flex-wrap lg:items-center lg:justify-end">
                <select
                  className="input min-w-[210px] text-sm"
                  value={flowComponentFilterId}
                  onChange={(event) =>
                    setFlowComponentFilterId(event.target.value)
                  }
                >
                  <option value="all">All visible components</option>
                  {flowComponentFilterOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <div className="flex flex-wrap items-center gap-3 rounded-md border border-gray-200 dark:border-gray-700 px-3 py-2 self-start lg:self-auto bg-white dark:bg-gray-800">
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={enabledFlowGroupings.source}
                      onChange={() => toggleFlowGrouping("source")}
                    />
                    By source
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={enabledFlowGroupings.destination}
                      onChange={() => toggleFlowGrouping("destination")}
                    />
                    By destination
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={showDisabledFlows}
                      onChange={() => setShowDisabledFlows(!showDisabledFlows)}
                    />
                    Show disabled
                  </label>
                </div>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    const id = crypto.randomUUID();
                    const fallbackComponent = allComponents[0]?.id ?? "";
                    updateDraft({
                      dataFlows: [
                        ...draft.dataFlows,
                        {
                          id,
                          fromComponentId: fallbackComponent,
                          toComponentId: fallbackComponent,
                          protocol: "https",
                          authMethod: "jwt",
                          carriesSensitiveData: true,
                          notes: "",
                        },
                      ],
                    });
                    setEditingFlowId(id);
                  }}
                >
                  <Plus className="h-4 w-4 mr-1" /> Add flow
                </button>
              </div>
            </div>

            {draft.dataFlows.length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Add key flows like frontend to API, API to database, and API to
                external services.
              </p>
            )}

            {draft.dataFlows.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700 text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      <th className="py-2 w-24 font-medium">Actions</th>
                      {(
                        [
                          ["from", "From"],
                          ["to", "To"],
                          ["protocol", "Protocol"],
                          ["auth", "Auth"],
                          ["sensitive", "Sensitive"],
                        ] as const
                      ).map(([col, label]) => (
                        <th
                          key={col}
                          className="py-2 pr-2 font-medium cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                          onClick={() => {
                            if (flowSortColumn === col) {
                              setFlowSortAsc(!flowSortAsc);
                            } else {
                              setFlowSortColumn(col);
                              setFlowSortAsc(true);
                            }
                          }}
                        >
                          <span className="inline-flex items-center gap-1">
                            {label}
                            {flowSortColumn === col &&
                              (flowSortAsc ? (
                                <ChevronUp className="h-3 w-3" />
                              ) : (
                                <ChevronDown className="h-3 w-3" />
                              ))}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {(() => {
                      const disabledComponentIds = new Set(
                        draft.components
                          .filter((c) => c.disabled)
                          .map((c) => c.id),
                      );
                      const visibleFlowIds =
                        flowComponentFilterId === "all"
                          ? null
                          : new Set(
                              visibleDataFlowGroups.flatMap((g) =>
                                g.flows.map((f) => f.id),
                              ),
                            );
                      const filteredFlows = draft.dataFlows.filter((f) => {
                        const hasDisabledComponent =
                          disabledComponentIds.has(f.fromComponentId) ||
                          disabledComponentIds.has(f.toComponentId);
                        if (hasDisabledComponent && !showDisabledFlows)
                          return false;
                        if (visibleFlowIds === null) return true;
                        return visibleFlowIds.has(f.id);
                      });

                      if (filteredFlows.length === 0) {
                        return (
                          <tr>
                            <td
                              colSpan={6}
                              className="py-6 text-center text-sm text-gray-500 dark:text-gray-400"
                            >
                              No flows match the selected component filter.
                            </td>
                          </tr>
                        );
                      }

                      const getCompName = (id: string) =>
                        allComponents
                          .find((c) => c.id === id)
                          ?.name?.toLowerCase() ?? id;

                      const sortedFlows = [...filteredFlows].sort((a, b) => {
                        let cmp = 0;
                        switch (flowSortColumn) {
                          case "from":
                            cmp = getCompName(a.fromComponentId).localeCompare(
                              getCompName(b.fromComponentId),
                            );
                            break;
                          case "to":
                            cmp = getCompName(a.toComponentId).localeCompare(
                              getCompName(b.toComponentId),
                            );
                            break;
                          case "protocol":
                            cmp = a.protocol
                              .toLowerCase()
                              .localeCompare(b.protocol.toLowerCase());
                            break;
                          case "auth":
                            cmp = a.authMethod
                              .toLowerCase()
                              .localeCompare(b.authMethod.toLowerCase());
                            break;
                          case "sensitive":
                            cmp =
                              Number(b.carriesSensitiveData) -
                              Number(a.carriesSensitiveData);
                            break;
                        }
                        return flowSortAsc ? cmp : -cmp;
                      });

                      return sortedFlows.map((flow) => {
                        const fromComp = allComponents.find(
                          (c) => c.id === flow.fromComponentId,
                        );
                        const toComp = allComponents.find(
                          (c) => c.id === flow.toComponentId,
                        );
                        const isFlowDisabled =
                          flow.disabled ||
                          disabledComponentIds.has(flow.fromComponentId) ||
                          disabledComponentIds.has(flow.toComponentId);
                        return (
                          <tr
                            key={flow.id}
                            className={`transition-colors cursor-pointer ${
                              isFlowDisabled
                                ? "opacity-40"
                                : effectiveSelectedFlowId === flow.id
                                  ? "bg-primary-50/40 dark:bg-primary-900/10"
                                  : "hover:bg-gray-50 dark:hover:bg-gray-800/50"
                            }`}
                            onDoubleClick={() => setEditingFlowId(flow.id)}
                            onClick={() => {
                              setSelectedFlowId(flow.id);
                              const group = visibleDataFlowGroups.find((g) =>
                                g.flows.some((f) => f.id === flow.id),
                              );
                              if (group) {
                                const element = document.getElementById(
                                  `flow-group-${group.groupKey}`,
                                );
                                const container = element?.closest(
                                  "[data-flow-diagrams]",
                                );
                                if (element && container) {
                                  container.scrollTo({
                                    top:
                                      element.offsetTop -
                                      (container as HTMLElement).offsetTop,
                                    behavior: "smooth",
                                  });
                                }
                              }
                            }}
                          >
                            <td className="py-2">
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${flow.disabled ? "text-amber-500 hover:text-amber-600" : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"}`}
                                  title={flow.disabled ? "Enable" : "Disable"}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    updateDraft({
                                      dataFlows: draft.dataFlows.map((f) =>
                                        f.id === flow.id
                                          ? { ...f, disabled: !f.disabled }
                                          : f,
                                      ),
                                    });
                                  }}
                                >
                                  {flow.disabled ? (
                                    <EyeOff className="h-4 w-4" />
                                  ) : (
                                    <Eye className="h-4 w-4" />
                                  )}
                                </button>
                                <button
                                  type="button"
                                  className="p-1 rounded text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                  title="Edit"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setEditingFlowId(flow.id);
                                  }}
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  className="p-1 rounded text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                  title="Delete"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    updateDraft({
                                      dataFlows: draft.dataFlows.filter(
                                        (current) => current.id !== flow.id,
                                      ),
                                    });
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                            <td className="py-2 pr-2 text-gray-900 dark:text-gray-100 max-w-[140px] truncate">
                              {fromComp?.name ?? flow.fromComponentId}
                            </td>
                            <td className="py-2 pr-2 text-gray-900 dark:text-gray-100 max-w-[140px] truncate">
                              {toComp?.name ?? flow.toComponentId}
                            </td>
                            <td className="py-2 pr-2 text-gray-600 dark:text-gray-300">
                              {flow.protocol || "—"}
                            </td>
                            <td className="py-2 pr-2 text-gray-600 dark:text-gray-300 max-w-[100px] truncate">
                              {flow.authMethod || "—"}
                            </td>
                            <td className="py-2 pr-2">
                              {flow.carriesSensitiveData ? (
                                <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                                  Yes
                                </span>
                              ) : (
                                <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                                  No
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            )}

            {(() => {
              const editingFlow = draft.dataFlows.find(
                (f) => f.id === editingFlowId,
              );
              if (!editingFlow) return null;
              return (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-3xl mx-4 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        Edit data flow
                      </h3>
                      <button
                        type="button"
                        onClick={() => setEditingFlowId(null)}
                        className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="p-4 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            From
                          </label>
                          <select
                            className="input w-full"
                            value={editingFlow.fromComponentId}
                            onChange={(event) =>
                              updateDraft({
                                dataFlows: draft.dataFlows.map((f) =>
                                  f.id === editingFlow.id
                                    ? {
                                        ...f,
                                        fromComponentId: event.target.value,
                                      }
                                    : f,
                                ),
                              })
                            }
                          >
                            {allComponents.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name} ({kindLabel(c.kind)})
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            To
                          </label>
                          <select
                            className="input w-full"
                            value={editingFlow.toComponentId}
                            onChange={(event) =>
                              updateDraft({
                                dataFlows: draft.dataFlows.map((f) =>
                                  f.id === editingFlow.id
                                    ? {
                                        ...f,
                                        toComponentId: event.target.value,
                                      }
                                    : f,
                                ),
                              })
                            }
                          >
                            {allComponents.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name} ({kindLabel(c.kind)})
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Protocol
                          </label>
                          <input
                            className="input w-full"
                            value={editingFlow.protocol}
                            onChange={(event) =>
                              updateDraft({
                                dataFlows: draft.dataFlows.map((f) =>
                                  f.id === editingFlow.id
                                    ? { ...f, protocol: event.target.value }
                                    : f,
                                ),
                              })
                            }
                            placeholder="e.g. HTTPS, gRPC"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Auth method
                          </label>
                          <input
                            className="input w-full"
                            value={editingFlow.authMethod}
                            onChange={(event) =>
                              updateDraft({
                                dataFlows: draft.dataFlows.map((f) =>
                                  f.id === editingFlow.id
                                    ? { ...f, authMethod: event.target.value }
                                    : f,
                                ),
                              })
                            }
                            placeholder="e.g. JWT, mTLS, none"
                          />
                        </div>
                      </div>
                      <label className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                        <input
                          type="checkbox"
                          className="mr-2"
                          checked={editingFlow.carriesSensitiveData}
                          onChange={(event) =>
                            updateDraft({
                              dataFlows: draft.dataFlows.map((f) =>
                                f.id === editingFlow.id
                                  ? {
                                      ...f,
                                      carriesSensitiveData:
                                        event.target.checked,
                                    }
                                  : f,
                              ),
                            })
                          }
                        />
                        Carries sensitive data
                      </label>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Notes
                        </label>
                        <textarea
                          className="input w-full min-h-[240px]"
                          value={editingFlow.notes}
                          onChange={(event) =>
                            updateDraft({
                              dataFlows: draft.dataFlows.map((f) =>
                                f.id === editingFlow.id
                                  ? { ...f, notes: event.target.value }
                                  : f,
                              ),
                            })
                          }
                          placeholder="Notes"
                        />
                      </div>
                    </div>
                    <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!editingFlow.disabled}
                          onChange={() =>
                            updateDraft({
                              dataFlows: draft.dataFlows.map((f) =>
                                f.id === editingFlow.id
                                  ? { ...f, disabled: !f.disabled }
                                  : f,
                              ),
                            })
                          }
                        />
                        Enabled in diagram
                      </label>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setEditingFlowId(null)}
                      >
                        Done
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          <div
            className={`space-y-4 ${
              filteredFlowDiagram
                ? ""
                : "xl:sticky xl:top-16 xl:h-[calc(100vh-4.5rem)]"
            }`}
          >
            <div
              className={`card space-y-3 ${
                filteredFlowDiagram ? "" : "xl:h-full xl:flex xl:flex-col"
              }`}
            >
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Grouped diagrams
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {activeFlowGroupingModes.length === 2
                    ? "Source and destination perspectives"
                    : activeFlowGroupingModes[0] === "source"
                      ? "One source to many destinations"
                      : "Many sources into one destination"}
                </p>
              </div>
              {filteredFlowDiagram ? (
                <div className="pr-1">
                  <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-900">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {selectedFlowComponentFilterLabel ??
                              "Filtered flows"}
                          </p>
                          <span className="rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:text-gray-300">
                            Combined diagram
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {filteredFlowDiagram.flowCount} related{" "}
                          {filteredFlowDiagram.flowCount === 1
                            ? "flow"
                            : "flows"}
                        </p>
                      </div>
                    </div>

                    <div className="relative mt-3 h-[420px] 2xl:h-[480px] rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800">
                      <button
                        type="button"
                        onClick={() =>
                          setFullscreenFlowGroupKey("__filtered__")
                        }
                        className="absolute top-2 right-2 z-10 p-1 rounded bg-white/80 dark:bg-gray-800/80 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-white dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-700"
                        title="Fullscreen"
                      >
                        <Maximize2 className="h-4 w-4" />
                      </button>
                      <ReactFlow
                        className="h-full"
                        nodes={filteredFlowDiagram.graph.nodes}
                        edges={filteredFlowDiagram.graph.edges}
                        fitView
                        fitViewOptions={{ padding: 0.08 }}
                        nodesDraggable={false}
                        nodesConnectable={false}
                        elementsSelectable={false}
                        zoomOnScroll
                        panOnDrag
                        minZoom={0.35}
                        maxZoom={1.6}
                      >
                        <Background gap={18} size={1} />
                      </ReactFlow>
                    </div>

                    <div className="mt-3 flex max-h-36 flex-wrap content-start gap-2 overflow-y-auto pr-1">
                      {filteredFlowDiagram.relatedFlows.map((flow) => (
                        <button
                          key={flow.id}
                          type="button"
                          className={`rounded-md border px-2 py-1 text-xs text-left ${
                            effectiveSelectedFlowId === flow.id
                              ? "border-primary-400 dark:border-primary-600 bg-primary-50 dark:bg-primary-900/20 text-primary-800 dark:text-primary-200"
                              : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                          }`}
                          onClick={() => setSelectedFlowId(flow.id)}
                        >
                          {flow.label}
                          <span className="ml-1 text-gray-500 dark:text-gray-400">
                            {flow.protocol}
                            {flow.authMethod ? ` • ${flow.authMethod}` : ""}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : selectedDisabledFlowDiagram ? (
                <div className="pr-1">
                  <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-900">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Disabled flow
                      </p>
                      <span className="rounded-full bg-amber-100 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-300">
                        Hidden from architecture
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {allComponents.find(
                        (c) =>
                          c.id ===
                          selectedDisabledFlowDiagram.flow.fromComponentId,
                      )?.name ?? "Unknown"}
                      {" → "}
                      {allComponents.find(
                        (c) =>
                          c.id ===
                          selectedDisabledFlowDiagram.flow.toComponentId,
                      )?.name ?? "Unknown"}
                      {" • "}
                      {selectedDisabledFlowDiagram.flow.protocol}
                    </p>
                    <div className="relative mt-3 h-[300px] rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800">
                      <ReactFlow
                        className="h-full"
                        nodes={selectedDisabledFlowDiagram.graph.nodes}
                        edges={selectedDisabledFlowDiagram.graph.edges}
                        fitView
                        fitViewOptions={{ padding: 0.15 }}
                        nodesDraggable={false}
                        nodesConnectable={false}
                        elementsSelectable={false}
                        zoomOnScroll
                        panOnDrag
                        minZoom={0.35}
                        maxZoom={1.6}
                      >
                        <Background gap={18} size={1} />
                      </ReactFlow>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  data-flow-diagrams
                  className="space-y-3 overflow-y-auto pr-1 xl:flex-1 xl:min-h-0"
                >
                  {visibleDataFlowGroups.map((group) => (
                    <div
                      key={group.groupKey}
                      id={`flow-group-${group.groupKey}`}
                      className="rounded-md border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-900"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {group.componentName}
                            </p>
                            <span className="rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:text-gray-300">
                              {group.groupingLabel}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {group.flowCount}{" "}
                            {group.groupingMode === "source"
                              ? "outgoing"
                              : "incoming"}{" "}
                            {group.flowCount === 1 ? "flow" : "flows"}
                          </p>
                        </div>
                      </div>

                      <div className="relative mt-3 h-[420px] xl:h-[clamp(460px,56vh,780px)] rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800">
                        <button
                          type="button"
                          onClick={() =>
                            setFullscreenFlowGroupKey(group.groupKey)
                          }
                          className="absolute top-2 right-2 z-10 p-1 rounded bg-white/80 dark:bg-gray-800/80 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-white dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-700"
                          title="Fullscreen"
                        >
                          <Maximize2 className="h-4 w-4" />
                        </button>
                        <ReactFlow
                          nodes={group.graph.nodes}
                          edges={group.graph.edges}
                          fitView
                          fitViewOptions={{ padding: 0.12 }}
                          nodesDraggable={false}
                          nodesConnectable={false}
                          elementsSelectable={false}
                          zoomOnScroll
                          panOnDrag
                          minZoom={0.45}
                          maxZoom={1.6}
                        >
                          <Background gap={18} size={1} />
                        </ReactFlow>
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        {group.relatedComponents.map((component) => (
                          <button
                            key={component.id}
                            type="button"
                            className={`rounded-md border px-2 py-1 text-xs text-left ${
                              effectiveSelectedFlowId === component.id
                                ? "border-primary-400 dark:border-primary-600 bg-primary-50 dark:bg-primary-900/20 text-primary-800 dark:text-primary-200"
                                : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                            }`}
                            onClick={() => setSelectedFlowId(component.id)}
                          >
                            {component.name}
                            <span className="ml-1 text-gray-500 dark:text-gray-400">
                              {component.protocol}
                              {component.authMethod
                                ? ` • ${component.authMethod}`
                                : ""}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {fullscreenFlowGroupKey &&
            (() => {
              const isFiltered = fullscreenFlowGroupKey === "__filtered__";
              const graph = isFiltered
                ? filteredFlowDiagram?.graph
                : visibleDataFlowGroups.find(
                    (g) => g.groupKey === fullscreenFlowGroupKey,
                  )?.graph;
              if (!graph) return null;
              const title = isFiltered
                ? `Filtered: ${selectedFlowComponentFilterLabel ?? "Filtered flows"}`
                : (visibleDataFlowGroups.find(
                    (g) => g.groupKey === fullscreenFlowGroupKey,
                  )?.componentName ?? "Diagram");
              return (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
                  <div className="bg-white dark:bg-gray-800 rounded-lg w-[95vw] h-[90vh] mx-4 flex flex-col border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {title}
                      </h3>
                      <button
                        type="button"
                        onClick={() => setFullscreenFlowGroupKey(null)}
                        className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="flex-1 min-h-0">
                      <ReactFlow
                        nodes={graph.nodes}
                        edges={graph.edges}
                        fitView
                        fitViewOptions={{ padding: 0.15 }}
                        nodesDraggable={false}
                        nodesConnectable={false}
                        elementsSelectable={false}
                        zoomOnScroll
                        panOnDrag
                        minZoom={0.2}
                        maxZoom={2.5}
                      >
                        <Controls
                          showInteractive={false}
                          position="bottom-right"
                        />
                        <Background gap={20} size={1} />
                      </ReactFlow>
                    </div>
                  </div>
                </div>
              );
            })()}
        </div>
      );
    }

    if (stepIndex === 6) {
      return (
        <div className="space-y-6">
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
              Review snapshot
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="rounded-md border border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Repositories
                </p>
                <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">
                  {draft.repositories.length}
                </p>
              </div>
              <div className="rounded-md border border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Components
                </p>
                <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">
                  {draft.components.length +
                    (hasPlatformTopology ? PLATFORM_COMPONENTS.length : 0)}
                </p>
              </div>
              <div className="rounded-md border border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Assets
                </p>
                <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">
                  {draft.assets.length}
                </p>
              </div>
              <div className="rounded-md border border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Data flows
                </p>
                <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">
                  {draft.dataFlows.length}
                </p>
              </div>
            </div>
          </div>

          <div className="card space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Assumptions and notes
            </h3>
            <textarea
              className="input min-h-[140px]"
              value={draft.assumptions}
              onChange={(event) =>
                updateDraft({ assumptions: event.target.value })
              }
              placeholder="Capture trust boundaries, assumptions, and known gaps."
            />
          </div>
        </div>
      );
    }

    if (stepIndex === 1) {
      if (!threatModelId) {
        return (
          <div className="card p-8 text-center">
            <Sparkles className="h-8 w-8 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 dark:text-gray-300 font-medium">
              Save your threat model first to customize prompts
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Click &ldquo;Save&rdquo; on the Review step, then return here to
              edit the AI prompts used for analysis.
            </p>
          </div>
        );
      }

      return (
        <div className="space-y-4">
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              AI analysis prompts
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Customize the system prompts used by the security agent for this
              session. Unmodified prompts use the global defaults.
            </p>
          </div>

          {sessionPrompts.length === 0 && (
            <div className="card p-6 text-center text-sm text-gray-500 dark:text-gray-400">
              Loading prompts...
            </div>
          )}

          {sessionPrompts.map((prompt) => {
            const isExpanded = expandedPromptSlug === prompt.prompt_slug;
            const editValue = promptEdits[prompt.prompt_slug] ?? prompt.content;
            const isDirty =
              prompt.prompt_slug in promptEdits &&
              promptEdits[prompt.prompt_slug] !== prompt.content;

            return (
              <div key={prompt.prompt_slug} className="card overflow-hidden">
                <button
                  type="button"
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  onClick={() =>
                    setExpandedPromptSlug(
                      isExpanded ? null : prompt.prompt_slug,
                    )
                  }
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Sparkles className="h-4 w-4 text-primary-500 shrink-0" />
                    <div className="min-w-0">
                      <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                        {prompt.name}
                      </span>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {prompt.description}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {prompt.is_override && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium">
                        Modified
                      </span>
                    )}
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 space-y-3">
                    <textarea
                      className="input font-mono text-xs min-h-[200px] leading-relaxed"
                      value={editValue}
                      readOnly={!canEditPrompts}
                      onChange={(e) =>
                        setPromptEdits((prev) => ({
                          ...prev,
                          [prompt.prompt_slug]: e.target.value,
                        }))
                      }
                    />
                    {prompt.variables && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        <span className="font-medium">Variables:</span>{" "}
                        {(() => {
                          try {
                            const vars =
                              typeof prompt.variables === "string"
                                ? JSON.parse(prompt.variables)
                                : prompt.variables;
                            if (Array.isArray(vars)) {
                              return vars
                                .map((v: any) => `{{${v.name}}}`)
                                .join(", ");
                            }
                          } catch {}
                          return "none";
                        })()}
                      </div>
                    )}
                    {canEditPrompts && (
                      <div className="flex items-center justify-between">
                        <button
                          type="button"
                          className="btn btn-secondary text-xs"
                          disabled={
                            !prompt.is_override || promptResetMutation.isLoading
                          }
                          onClick={() => {
                            setPromptEdits((prev) => {
                              const next = { ...prev };
                              delete next[prompt.prompt_slug];
                              return next;
                            });
                            promptResetMutation.mutate(prompt.prompt_slug);
                          }}
                        >
                          Reset to default
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary text-xs"
                          disabled={!isDirty || promptSaveMutation.isLoading}
                          onClick={() =>
                            promptSaveMutation.mutate({
                              slug: prompt.prompt_slug,
                              content: editValue,
                            })
                          }
                        >
                          <Save className="h-3 w-3 mr-1" /> Save prompt
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    if (stepIndex === 7) {
      return (
        <div className="space-y-6">
          <div className="card space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Architecture diagram
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {hasPlatformTopology
                  ? "Split view with auto-layout (left-to-right)"
                  : `${fullGraph.nodes.length} components, ${fullGraph.edges.length} flows`}
              </p>
            </div>
            {hasPlatformTopology ? (
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      1) Edge path: User to HTTPRoute
                    </p>
                    <button
                      type="button"
                      onClick={() => setFullscreenDiagram("edge")}
                      className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      title="Fullscreen"
                    >
                      <Maximize2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="h-[300px] rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden bg-gray-50 dark:bg-gray-900">
                    <ReactFlow
                      nodes={edgePathGraph.nodes}
                      edges={edgePathGraph.edges}
                      fitView
                      fitViewOptions={{ padding: 0.25 }}
                      nodesDraggable={false}
                      nodesConnectable={false}
                      elementsSelectable={false}
                      zoomOnScroll
                      panOnDrag
                      minZoom={0.35}
                      maxZoom={1.6}
                    >
                      <Controls
                        showInteractive={false}
                        position="bottom-right"
                      />
                      <Background gap={20} size={1} />
                    </ReactFlow>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      2) Application/internal path: HTTPRoute to downstream
                      services
                    </p>
                    <button
                      type="button"
                      onClick={() => setFullscreenDiagram("downstream")}
                      className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      title="Fullscreen"
                    >
                      <Maximize2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="h-[360px] rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden bg-gray-50 dark:bg-gray-900">
                    <ReactFlow
                      nodes={downstreamPathGraph.nodes}
                      edges={downstreamPathGraph.edges}
                      fitView
                      fitViewOptions={{ padding: 0.2 }}
                      nodesDraggable={false}
                      nodesConnectable={false}
                      elementsSelectable={false}
                      zoomOnScroll
                      panOnDrag
                      minZoom={0.35}
                      maxZoom={1.6}
                    >
                      <Controls
                        showInteractive={false}
                        position="bottom-right"
                      />
                      <Background gap={20} size={1} />
                    </ReactFlow>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-end mb-2">
                  <button
                    type="button"
                    onClick={() => setFullscreenDiagram("edge")}
                    className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    title="Fullscreen"
                  >
                    <Maximize2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="h-[600px] rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden bg-gray-50 dark:bg-gray-900">
                  <ReactFlow
                    nodes={fullGraph.nodes}
                    edges={fullGraph.edges}
                    fitView
                    fitViewOptions={{ padding: 0.15 }}
                    nodesDraggable={false}
                    nodesConnectable={false}
                    elementsSelectable={false}
                    zoomOnScroll
                    panOnDrag
                    minZoom={0.15}
                    maxZoom={2.0}
                  >
                    <Controls showInteractive={false} position="bottom-right" />
                    <Background gap={20} size={1} />
                  </ReactFlow>
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-2 text-xs text-gray-600 dark:text-gray-300">
              <span className="px-2 py-1 rounded-md bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800">
                Frontend
              </span>
              <span className="px-2 py-1 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700">
                Backend
              </span>
              <span className="px-2 py-1 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700">
                Database
              </span>
              <span className="px-2 py-1 rounded-md bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-700">
                External
              </span>
              <span className="px-2 py-1 rounded-md bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700">
                Cache
              </span>
              <span className="px-2 py-1 rounded-md bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-700">
                Queue
              </span>
              <span className="px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                Other
              </span>
            </div>
          </div>
        </div>
      );
    }

    return <div className="space-y-6">{renderStrideAnalysis()}</div>;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {threatModel
              ? (threatModel.family?.name ?? threatModel.name)
              : "Threat Modelling"}
          </h1>
          {threatModel && (
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <select
                aria-label="Version"
                className="input w-auto py-1 font-mono text-sm"
                value={threatModel.id}
                onChange={(e) =>
                  navigate(`/threat-modelling/${e.target.value}`)
                }
              >
                {(siblingVersions.some((v) => v.id === threatModel.id)
                  ? siblingVersions
                  : [threatModel, ...siblingVersions]
                ).map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.version}
                    {v.archived_at ? " (archived)" : ""}
                  </option>
                ))}
              </select>
              {threatModel.source_version && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Based on {threatModel.source_version.version}
                </span>
              )}
              {threatModel.archived_at && (
                <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  Archived
                </span>
              )}
            </div>
          )}
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            {threatModelId
              ? `Phase: ${activeArtifactInfo?.phase ?? threatModel?.phase ?? "—"} · Status: ${activeArtifactInfo?.status ?? threatModel?.status ?? "—"}`
              : "New threat model"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {threatModel && canCreateThreatModel(user) && (
            <button
              type="button"
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors"
              onClick={() => setShowNewVersion(true)}
            >
              <GitBranch className="h-4 w-4" />
              New version
            </button>
          )}
          {/* Hidden for now — kept for potential future use */}
          {false && (
            <label className="cursor-pointer flex items-center gap-2 px-3 py-2 text-sm font-medium bg-zinc-700 hover:bg-zinc-600 text-white rounded-md transition-colors">
              <Upload className="h-4 w-4" />
              Load File
              <input
                type="file"
                accept=".json"
                className="hidden"
                onChange={handleFileImport}
              />
            </label>
          )}
          {false && (
            <button
              type="button"
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-zinc-700 hover:bg-zinc-600 text-white rounded-md transition-colors"
              onClick={handleExport}
            >
              <Download className="h-4 w-4" />
              Export
            </button>
          )}
          {!canEdit && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Read-only
            </span>
          )}
          {canEdit && (
            <>
              <button
                type="button"
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-50"
                onClick={handleSaveToBackend}
                disabled={
                  saveMutation.isLoading || saveAndAnalyseMutation.isLoading
                }
              >
                <Save className="h-4 w-4" />
                {saveMutation.isLoading ? "Saving..." : "Save"}
              </button>
              <button
                type="button"
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors disabled:opacity-50"
                onClick={handleSaveAndAnalyse}
                disabled={
                  saveMutation.isLoading || saveAndAnalyseMutation.isLoading
                }
              >
                <Play className="h-4 w-4" />
                {saveAndAnalyseMutation.isLoading
                  ? "Analysing..."
                  : "Save & Analyse"}
              </button>
            </>
          )}
          {threatModelId && canEdit && (
            <div className="relative">
              <button
                type="button"
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors disabled:opacity-50"
                onClick={() => setShowResetConfirm(true)}
                disabled={resetModelMutation.isLoading}
              >
                <RotateCcw className="h-4 w-4" />
                {resetModelMutation.isLoading ? "Resetting..." : "Reset"}
              </button>
              {showResetConfirm && (
                <div className="absolute right-0 top-full mt-2 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 w-72">
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                    This will clear all stages, findings, and artifacts. Config
                    and prompts will be kept.
                  </p>
                  <div className="flex gap-2 justify-end">
                    <button
                      type="button"
                      className="px-3 py-1.5 text-sm rounded-md bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      onClick={() => setShowResetConfirm(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="px-3 py-1.5 text-sm rounded-md bg-red-600 hover:bg-red-700 text-white transition-colors"
                      onClick={() => resetModelMutation.mutate()}
                    >
                      Confirm Reset
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {threatModel?.archived_at && (
        <div className="flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
          <Archive className="mt-0.5 h-4 w-4 shrink-0" />
          This version is archived. Its content and product links are kept, but
          it is hidden from default lists and cannot be newly linked or copied.
        </div>
      )}
      {threatModel?.results_inherited && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
          <History className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Results inherited from{" "}
            {threatModel.inherited_from?.version ?? "another version"}. No
            analysis has run for this version yet; rerunning a stage replaces
            the inherited results for that stage.
          </span>
        </div>
      )}
      {threatModel?.version_summary && (
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {threatModel.version_summary}
        </p>
      )}

      {showNewVersion && threatModel && (
        <ThreatModelVersionDialog
          anchorId={threatModel.id}
          familyName={threatModel.family?.name ?? threatModel.name}
          defaultSourceId={threatModel.archived_at ? undefined : threatModel.id}
          onClose={() => setShowNewVersion(false)}
        />
      )}

      <div className="space-y-6">
        <div>
          <div className="card p-4">
            <div className="grid grid-cols-2 md:grid-cols-8 gap-2">
              {STEPS.map((step, index) => {
                const isCurrent = index === stepIndex;
                const isCompleted = index < stepIndex;
                const StepIcon = STEP_ICONS[step];
                return (
                  <button
                    key={step}
                    type="button"
                    onClick={() => setStepIndex(index)}
                    className={`rounded-md px-3 py-2 text-sm font-medium border transition-colors ${
                      isCurrent
                        ? "bg-primary-600 text-white border-primary-600"
                        : isCompleted
                          ? "bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border-primary-200 dark:border-primary-800"
                          : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                    }`}
                  >
                    <span className="flex items-center justify-center gap-2">
                      <StepIcon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{step}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div>
          {renderStepContent()}

          <div className="mt-4 flex items-center justify-between">
            <button
              type="button"
              className="btn btn-secondary"
              disabled={stepIndex === 0}
              onClick={() =>
                setStepIndex((current) => Math.max(0, current - 1))
              }
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Previous
            </button>
            {stepIndex < STEPS.length - 1 && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() =>
                  setStepIndex((current) =>
                    Math.min(STEPS.length - 1, current + 1),
                  )
                }
              >
                Next <ArrowRight className="h-4 w-4 ml-1" />
              </button>
            )}
          </div>
        </div>
      </div>

      {fullscreenDiagram && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-[95vw] h-[90vh] mx-4 flex flex-col border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {!hasPlatformTopology
                  ? "Architecture diagram"
                  : fullscreenDiagram === "edge"
                    ? "Edge path: User to HTTPRoute"
                    : "Application/internal path: HTTPRoute to downstream services"}
              </h3>
              <button
                type="button"
                onClick={() => setFullscreenDiagram(null)}
                className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 min-h-0">
              <ReactFlow
                nodes={
                  !hasPlatformTopology
                    ? fullGraph.nodes
                    : fullscreenDiagram === "edge"
                      ? edgePathGraph.nodes
                      : downstreamPathGraph.nodes
                }
                edges={
                  !hasPlatformTopology
                    ? fullGraph.edges
                    : fullscreenDiagram === "edge"
                      ? edgePathGraph.edges
                      : downstreamPathGraph.edges
                }
                fitView
                fitViewOptions={{ padding: 0.15 }}
                nodesDraggable={false}
                nodesConnectable={false}
                elementsSelectable={false}
                zoomOnScroll
                panOnDrag
                minZoom={0.2}
                maxZoom={2.5}
              >
                <Controls showInteractive={false} position="bottom-right" />
                <Background gap={20} size={1} />
              </ReactFlow>
            </div>
            <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 flex flex-wrap gap-2 text-xs text-gray-600 dark:text-gray-300 shrink-0">
              <span className="px-2 py-1 rounded-md bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800">
                Frontend
              </span>
              <span className="px-2 py-1 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700">
                Backend
              </span>
              <span className="px-2 py-1 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700">
                Database
              </span>
              <span className="px-2 py-1 rounded-md bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-700">
                External
              </span>
              <span className="px-2 py-1 rounded-md bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700">
                Cache
              </span>
              <span className="px-2 py-1 rounded-md bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-700">
                Queue
              </span>
              <span className="px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                Other
              </span>
            </div>
          </div>
        </div>
      )}

      {selectedFinding && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl mx-4 max-h-[85vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Threat Finding Details
              </h3>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => setSelectedFinding(null)}
              >
                Close
              </button>
            </div>

            <div className="p-4 space-y-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Finding Name
                </p>
                <p className="mt-1 text-sm text-gray-900 dark:text-gray-100 font-medium">
                  {selectedFinding.findingName}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-900">
                  <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Control Type
                  </p>
                  <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                    {selectedFinding.controlType}
                  </p>
                </div>
                <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-900">
                  <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    STRIDE / CWE
                  </p>
                  <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                    {selectedFinding.stride} / {selectedFinding.cwe}
                  </p>
                </div>
              </div>

              <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-900">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Analysis Notes
                </p>
                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                  {selectedFinding.details}
                </p>
              </div>

              <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-900">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Recommended Counter Measures
                </p>
                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                  {selectedFinding.counterMeasures}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
